import React from 'react';
import { fetchWrapper, fetchUploadFileWrapper } from '../../APIManager';
const TopicsStateContext = /*#__PURE__*/React.createContext();
const TopicsDispatchContext = /*#__PURE__*/React.createContext();
const initialState = {
  latest_topics_users: [],
  // all users returned from latest topics API calls
  refresh_replies_topic_id: null,
  refresh_replies_topic_timestamp: null,
  force_reload_topics: false,
  participants: [],
  flagged_post_id: null,
  update_watching_status_timestamp: null
};

/** REDUCER **/
function TopicsReducer(state, action) {
  switch (action.type) {
    case 'SET_LATEST_TOPICS_USERS':
      return {
        ...state,
        latest_topics_users: action.payload
      };
    case 'SET_REFRESH_REPLIES_TOPIC_ID':
      return {
        ...state,
        refresh_replies_topic_id: action.payload
      };
    case 'SET_REFRESH_REPLIES_TOPIC_TIMESTAMP':
      return {
        ...state,
        refresh_replies_topic_timestamp: action.payload
      };
    case 'SET_FORCE_RELOAD_TOPICS':
      return {
        ...state,
        force_reload_topics: action.payload
      };
    case 'SET_PARTICIPANTS':
      return {
        ...state,
        participants: action.payload
      };
    case 'SET_FLAGGED_POST_ID':
      return {
        ...state,
        flagged_post_id: action.payload
      };
    case 'SET_UPDATE_WATCHING_STATUS_TOPIC_TIMESTAMP':
      return {
        ...state,
        update_watching_status_timestamp: action.payload
      };
    case 'GET_INITIAL':
      return initialState;
    default:
      {
        throw new Error('Unhandled action type caught');
      }
  }
}

/** PROVIDER **/
function TopicsProvider(_ref) {
  let {
    value,
    children
  } = _ref;
  const [state, dispatch] = React.useReducer(TopicsReducer, initialState);
  return /*#__PURE__*/React.createElement(TopicsStateContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(TopicsDispatchContext.Provider, {
    value: dispatch
  }, children));
}

/** STATE/DISPATCH HOOKS **/
function useTopicsState() {
  const context = React.useContext(TopicsStateContext);
  if (context === undefined) {
    throw new Error('useTopicsState must be used within a TopicsProvider');
  }
  return context;
}
function useTopicsDispatch() {
  const context = React.useContext(TopicsDispatchContext);
  if (context === undefined) {
    throw new Error('useTopicsDispatch must be used within a TopicsProvider');
  }
  return context;
}

/** API **/
const topicsAPI = {
  handleLatestTopicsByProduct: async (url, apiKey, product, productCategory, page) => {
    // this function retrieves initial list of topics when the app loads or selected channel changes

    if (!product) {
      throw new Error('argument product is required');
    }
    try {
      const productCategoryId = productCategory === null || productCategory === void 0 ? void 0 : productCategory.id;
      const ascending = false; // using discourse default
      const baseUrl = `/c/${product}/${productCategoryId}`;
      const queryParams = `ascending=${ascending}&page=${page}`;
      const slug = `${baseUrl}/l/latest.json?${queryParams}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Failed to handle latest topics');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  handleLatestTopicsBySelectedCategory: async (url, apiKey, product, productCategory, selectedCategory, page) => {
    // this function gets called when the scroll hits the bottom, regardless of the selected channel

    if (!product) {
      throw new Error('argument product is required');
    }
    try {
      var _productCategory$subc;
      const productCategoryId = productCategory === null || productCategory === void 0 ? void 0 : productCategory.id;
      const communityCategory = productCategory === null || productCategory === void 0 ? void 0 : (_productCategory$subc = productCategory.subcategory_list) === null || _productCategory$subc === void 0 ? void 0 : _productCategory$subc.find(sub => sub.name === `${product} Community`);
      const ascending = false;
      const baseUrl = `/c/${product}/${productCategoryId}/${communityCategory === null || communityCategory === void 0 ? void 0 : communityCategory.slug}/${communityCategory === null || communityCategory === void 0 ? void 0 : communityCategory.id}`;
      const queryParams = `ascending=${ascending}&page=${page}`;
      const slug = selectedCategory ? `${baseUrl}/${selectedCategory === null || selectedCategory === void 0 ? void 0 : selectedCategory.slug}/${selectedCategory === null || selectedCategory === void 0 ? void 0 : selectedCategory.id}/l/latest.json?${queryParams}` : `${baseUrl}/l/latest.json?${queryParams}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Failed to handle latest topics');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  getSingleTopicWithAllReplies: async (url, apiKey, topicId) => {
    try {
      // adding 'print=true' param will return all replies
      const slug = `/t/${topicId}.json?print=true`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error(`Topic with id ${topicId} not found`);
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  getSingleTopic: async (url, apiKey, topicId, lastPage) => {
    try {
      const slug = `/t/${topicId}.json${lastPage ? '?post_number=-1' : ''}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error(`Topic with id ${topicId} not found`);
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  getSingleTopicMoreReplies: async (url, apiKey, topicId, postIds) => {
    if (!postIds.length) return {
      post_stream: {
        posts: []
      }
    };
    try {
      // adding 'print=true' param will return all replies
      const slug = `/t/${topicId}/posts.json?${postIds.map(p => `post_ids[]=${p}`).join('&')}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error(`Topic with id ${topicId} not found`);
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  getTopicsDigest: async (url, apiKey, topicIds) => {
    try {
      const slug = `/topics/digest.json?topic_ids=${topicIds.join(',')}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get topics digest.');
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  createTopicReply: async (url, apiKey, data) => {
    try {
      const slug = '/posts.json';
      const response = await fetchWrapper(url, slug, apiKey, 'POST', data);
      if (!response || !response.created_at || !response.topic_id) {
        throw new Error('Could not create post.');
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  deleteTopic: async (url, apiKey, topicId) => {
    try {
      const slug = `/t/${topicId}.json`;
      const res = await fetchWrapper(url, slug, apiKey, 'DELETE');
      if (res && res.errors && res.errors.length) {
        throw new Error('Could not delete topic.');
      }
      return true;
    } catch (err) {
      return err;
    }
  },
  updateTopic: async (url, apiKey, topicId, data) => {
    try {
      const slug = `/t/-/${topicId}.json`;
      const response = await fetchWrapper(url, slug, apiKey, 'PUT', data);
      if (!response) {
        throw new Error('Could not update topic.');
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  updateReply: async (url, apiKey, replyId, data) => {
    try {
      const slug = `/posts/${replyId}.json`;
      const response = await fetchWrapper(url, slug, apiKey, 'PUT', data);
      if (!response) {
        throw new Error('Could not update reply.');
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  deleteReply: async (url, apiKey, replyId) => {
    try {
      const slug = `/posts/${replyId}.json`;
      const res = await fetchWrapper(url, slug, apiKey, 'DELETE');
      if (res && res.errors && res.errors.length) {
        throw new Error('Could not delete reply.');
      }
      return true;
    } catch (err) {
      return err;
    }
  },
  getReply: async (url, apiKey, replyId) => {
    try {
      const slug = `/posts/${replyId}.json`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get reply.');
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  likeReply: async (url, apiKey, type, replyId) => {
    try {
      const postActionTypeLike = 2; // id of 2 indicates 'Like'
      const slug = '/post_actions.json';
      const data = {
        id: type === 'reply' && replyId,
        post_action_type_id: postActionTypeLike
        // flag_topic: true // don't' need this for now
      };

      const response = await fetchWrapper(url, slug, apiKey, 'POST', data);
      if (!response) {
        throw new Error('Could not like reply.');
      }
      return response;
    } catch (err) {
      return err;
    }
  },
  unlikeReply: async (url, apiKey, replyId) => {
    try {
      const postActionTypeLike = 2;
      const slug = `/post_actions/${replyId}?post_action_type_id=${postActionTypeLike}`;
      const response = await fetchWrapper(url, slug, apiKey, 'DELETE');
      return response;
    } catch (err) {
      /*
      not checking if !response
      because DELETE request sometimes returns absolutely nothing
      even when it was successful
      */
      console.log('Could not unlike reply.');
      return err;
    }
  },
  flagPost: async (url, apiKey, postId, data) => {
    try {
      const slug = '/post_actions';
      const res = await fetchWrapper(url, slug, apiKey, 'POST', data);
      if (!res) {
        throw new Error('Failed to mark post as spam.');
      }
      return res;
    } catch (err) {
      return {};
    }
  },
  setNotificationLevel: async (url, apiKey, topicID, notificationLevel) => {
    /*
    notificationLevel:
    0: Muted
    1: Normal
    2: Tracking
    3: Watching
    */
    try {
      const slug = `/t/${topicID}/notifications`;
      const data = {
        notification_level: notificationLevel
      };
      const response = await fetchWrapper(url, slug, apiKey, 'POST', data);
      if (!response || response.success !== 'OK') {
        throw new Error('Could not handle updating notification level.');
      }
      return response;
    } catch (err) {
      return false;
    }
  },
  uploadFile: async (url, apiKey, formData) => {
    try {
      // const clientId = '';
      // const slug = `/uploads.json?client_id=${clientId}`;
      const slug = '/uploads.json';
      const response = await fetchUploadFileWrapper(url, slug, apiKey, formData);
      if (!response) {
        throw new Error('Could not upload the file.');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  getLatestTopicsByTag: async (url, category, categoryId, tag, page, apiKey) => {
    try {
      const ascending = false;
      const slug = `/tags/c/${category}/${categoryId}/${tag}/l/latest.json?ascending=${ascending}&page=${page}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (response && response.topic_list && response.topic_list.topics) {
        if (response.topic_list.topics.length) {
          return response;
        }
        return [];
      }
      throw new Error('Could not get latest topics sorted by tag.');
    } catch (err) {
      return [];
    }
  }
};
export { TopicsProvider, useTopicsState, useTopicsDispatch, topicsAPI };
//# sourceMappingURL=index.js.map